/// DiagramList: Shows open editor tabs as a file tree in the Project panel.
/// Displays BPMN and Node diagrams with dirty indicators and click-to-activate.

import { useCallback } from "react";
import { GitBranch, FileCode, Circle } from "lucide-react";
import { useEditorStore } from "@/stores/editorStore";
import type { EditorTab } from "@/types/editor";

// ---------------------------------------------------------------------------
// DiagramList (container)
// ---------------------------------------------------------------------------

export function DiagramList() {
  const tabs = useEditorStore((s) => s.tabs);
  const activeTabId = useEditorStore((s) => s.activeTabId);
  const setActiveTab = useEditorStore((s) => s.setActiveTab);

  const bpmnTabs = tabs.filter((t) => t.editorType === "bpmn");
  const nodeTabs = tabs.filter((t) => t.editorType === "nodeflow");

  if (tabs.length === 0) {
    return (
      <div className="px-3 py-6 text-center">
        <p className="text-xs text-zinc-500">
          No diagrams open. Create a new BPMN or Node flow to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {/* BPMN Diagrams */}
      {bpmnTabs.length > 0 && (
        <>
          <SectionHeader label="BPMN Diagrams" />
          {bpmnTabs.map((tab) => (
            <DiagramItem
              key={tab.id}
              tab={tab}
              isActive={tab.id === activeTabId}
              onActivate={setActiveTab}
            />
          ))}
        </>
      )}

      {/* Node Flow Diagrams */}
      {nodeTabs.length > 0 && (
        <>
          <SectionHeader label="Node Flows" className={bpmnTabs.length > 0 ? "mt-2" : undefined} />
          {nodeTabs.map((tab) => (
            <DiagramItem
              key={tab.id}
              tab={tab}
              isActive={tab.id === activeTabId}
              onActivate={setActiveTab}
            />
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DiagramItem
// ---------------------------------------------------------------------------

interface DiagramItemProps {
  tab: EditorTab;
  isActive: boolean;
  onActivate: (tabId: string) => void;
}

function DiagramItem({ tab, isActive, onActivate }: DiagramItemProps) {
  const handleClick = useCallback(() => {
    onActivate(tab.id);
  }, [onActivate, tab.id]);

  const isBPMN = tab.editorType === "bpmn";
  const displayName = tab.title.replace(/\.(bpmn|xml|json)$/i, "");

  return (
    <button
      onClick={handleClick}
      className={[
        "flex w-full items-center gap-1.5 px-2 py-1 text-sm rounded text-left transition-colors",
        isActive ? "bg-zinc-700/60 text-zinc-100" : "text-zinc-300 hover:bg-zinc-800",
      ].join(" ")}
    >
      {/* Editor type icon */}
      {isBPMN ? (
        <GitBranch size={14} className="text-blue-400 flex-shrink-0" />
      ) : (
        <FileCode size={14} className="text-green-400 flex-shrink-0" />
      )}

      {/* File name */}
      <span className="flex-1 truncate">{displayName}</span>

      {/* Dirty indicator */}
      {tab.dirty && (
        <Circle
          size={6}
          className="text-zinc-400 fill-zinc-400 flex-shrink-0"
          aria-label="Unsaved changes"
        />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SectionHeader({ label, className }: { label: string; className?: string }) {
  return (
    <div
      className={[
        "px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </div>
  );
}
